import React, { useState } from 'react';
import { Star, ExternalLink, CheckCircle2, ShieldCheck, Award } from 'lucide-react';
import { TestimonialsConfig } from '../types';

interface TrustpilotBadgeProps {
  config: TestimonialsConfig;
  darkMode?: boolean;
}

export const TrustpilotBadge: React.FC<TrustpilotBadgeProps> = ({ config, darkMode = true }) => {
  const [showModal, setShowModal] = useState(false);

  const rating = config.trustpilotRating || 4.9;
  const count = config.trustpilotReviewCount || 1280;
  const trustpilotUrl = config.trustpilotUrl || 'https://www.trustpilot.com/review/rheditography.net';

  return (
    <>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 py-4 px-6 rounded-2xl bg-slate-900/95 border border-slate-800 shadow-xl shadow-black/40 max-w-2xl mx-auto my-6 text-slate-100">
        
        {/* Trustpilot Logo & Rating Badge */}
        <div className="flex items-center gap-3">
          {/* Trustpilot Green Box Logo */}
          <div className="w-9 h-9 rounded-xl bg-[#00b67a] flex items-center justify-center text-white shadow-lg shadow-[#00b67a]/20 shrink-0">
            <Star className="w-5 h-5 fill-white text-white" />
          </div>
          <div className="text-left">
            <div className="flex items-center gap-1.5">
              <span className="text-base font-black text-white tracking-wide">Trustpilot</span>
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#00b67a]/20 text-[#00b67a] border border-[#00b67a]/30">
                Excellent
              </span>
            </div>
            {/* Trustpilot 5 Stars */}
            <div className="flex items-center gap-1 mt-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <div key={star} className="w-4 h-4 rounded bg-[#00b67a] flex items-center justify-center">
                  <Star className="w-3 h-3 fill-white text-white" />
                </div>
              ))}
              <span className="text-xs font-extrabold text-slate-200 ml-1.5">{rating.toFixed(1)} / 5.0</span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="hidden sm:block w-px h-10 bg-slate-800" />

        {/* Review Count & Action Button */}
        <div className="flex items-center justify-between sm:justify-end gap-4 w-full sm:w-auto">
          <div className="text-left sm:text-right">
            <p className="text-xs font-bold text-slate-300">
              Based on <span className="text-emerald-400 font-black">{count.toLocaleString()}+</span> reviews
            </p>
            <p className="text-[11px] font-medium text-slate-400 flex items-center gap-1 mt-0.5">
              <CheckCircle2 className="w-3 h-3 text-[#00b67a]" /> 100% Verified Customers
            </p>
          </div>

          <button
            onClick={() => {
              if (trustpilotUrl && trustpilotUrl.startsWith('http')) {
                window.open(trustpilotUrl, '_blank', 'noopener,noreferrer');
              } else {
                setShowModal(true);
              }
            }}
            className="px-3.5 py-2 rounded-xl text-xs font-black text-white bg-[#00b67a] hover:bg-[#00a06b] border border-[#00c987] shadow-md shadow-[#00b67a]/20 transition-all flex items-center gap-1.5 shrink-0 hover:scale-105 active:scale-95"
          >
            <span>View Trustpilot</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Trustpilot Information Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90">
          <div className="bg-slate-900 border border-slate-700 rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-5 text-slate-100 relative">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#00b67a] flex items-center justify-center text-white shadow-lg">
                <Award className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-black text-white">Trustpilot Verification</h3>
                <p className="text-xs text-slate-400">RH Editography Verified Service Standards</p>
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-slate-800/60 border border-slate-700/60 space-y-2 text-xs text-slate-300">
              <p className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#00b67a]" />
                <span>Overall Rating: <strong className="text-white">{rating} / 5.0 Stars</strong></span>
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#00b67a]" />
                <span>Verified Buyer Reviews: <strong className="text-white">{count.toLocaleString()} Total</strong></span>
              </p>
              <p className="flex items-center gap-2">
                <Star className="w-4 h-4 text-[#00b67a]" />
                <span>Primary Sources: Admin Managed Verified Digital Customers</span>
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => setShowModal(false)}
                className="px-5 py-2.5 rounded-xl text-xs font-bold bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 transition-all"
              >
                Close
              </button>
              <a
                href={trustpilotUrl}
                target="_blank"
                rel="noreferrer"
                className="px-5 py-2.5 rounded-xl text-xs font-black bg-[#00b67a] hover:bg-[#00a06b] text-white transition-all flex items-center gap-2 shadow-lg shadow-[#00b67a]/30"
              >
                <span>Visit Trustpilot.com</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
